import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import classNames from 'classnames';
import { useMediaQuery } from 'react-responsive';

import classes from './styles.module.scss';

export default function Tooltip({
  children,
  text,
  width,
  direction,
  disabled,
  noWrap,
  fixed,
  timeout = 300,
}) {
  const [isManualOpen, setIsManualOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const hadTouchStartRef = useRef(false);
  const tooltipIdRef = useRef(
    `tooltip-${Math.random().toString(36).substr(2, 9)}`
  );
  const triggerRef = useRef(null);

  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1024px)' });

  // Map direction to react-tooltip place prop
  const place = useMemo(() => {
    if (direction?.includes('top')) return 'top';
    if (direction?.includes('bottom')) return 'bottom';
    if (direction === 'left') return 'left';
    if (direction === 'right') return 'right';
    return 'bottom';
  }, [direction]);

  // Clone children to add data-tooltip-id
  const triggerElement = useMemo(() => {
    if (React.isValidElement(children)) {
      const originalRef = children.ref;
      return React.cloneElement(children, {
        'data-tooltip-id': tooltipIdRef.current,
        'data-tooltip-place': place,
        ref: (node) => {
          triggerRef.current = node;
          // Handle original ref if it exists
          if (typeof originalRef === 'function') {
            originalRef(node);
          } else if (originalRef) {
            originalRef.current = node;
          }
        },
        style: {
          ...children.props.style,
        },
      });
    }
    return (
      <span
        ref={triggerRef}
        data-tooltip-id={tooltipIdRef.current}
        data-tooltip-place={place}
      >
        {children}
      </span>
    );
  }, [children, place]);

  // Suppress ResizeObserver errors (common with react-tooltip)
  useEffect(() => {
    const errorHandler = (e) => {
      if (
        e.message?.includes('ResizeObserver loop') ||
        e.message === 'ResizeObserver loop limit exceeded'
      ) {
        e.stopImmediatePropagation();
        e.preventDefault();
        return false;
      }
      return undefined;
    };

    const unhandledRejectionHandler = (e) => {
      if (
        e.reason?.message?.includes('ResizeObserver loop') ||
        e.reason === 'ResizeObserver loop limit exceeded'
      ) {
        e.preventDefault();
        return false;
      }
      return undefined;
    };

    window.addEventListener('error', errorHandler);
    window.addEventListener('unhandledrejection', unhandledRejectionHandler);

    return () => {
      window.removeEventListener('error', errorHandler);
      window.removeEventListener(
        'unhandledrejection',
        unhandledRejectionHandler
      );
    };
  }, []);

  useEffect(() => {
    const handleGlobalPointerDown = (event) => {
      if (!triggerRef.current?.contains(event.target)) {
        setIsManualOpen(false);
        setIsHovered(false);
      }
    };

    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setIsManualOpen(false);
        setIsHovered(false);
      }
    };

    document.addEventListener('mousedown', handleGlobalPointerDown, true);
    document.addEventListener('touchstart', handleGlobalPointerDown, true);
    document.addEventListener('keydown', handleEsc, true);

    return () => {
      document.removeEventListener('mousedown', handleGlobalPointerDown, true);
      document.removeEventListener('touchstart', handleGlobalPointerDown, true);
      document.removeEventListener('keydown', handleEsc, true);
    };
  }, []);

  // Control tooltip visibility programmatically for manual open
  // Only use isOpen when manually opened, otherwise let react-tooltip handle hover
  useEffect(() => {
    if (isManualOpen) {
      setIsTooltipOpen(true);
    } else if (!isHovered) {
      setIsTooltipOpen(false);
    }
  }, [isManualOpen, isHovered]);

  return (
    <div
      className={classes.Tooltip}
      onMouseEnter={() => {
        if (!disabled && !isManualOpen) {
          setIsHovered(true);
        }
      }}
      onMouseLeave={() => {
        if (!disabled && !isManualOpen) {
          setIsHovered(false);
        }
      }}
      onClick={(e) => {
        if (disabled) return;
        if (hadTouchStartRef.current) return;
        const interactive = e.target.closest(
          'button, a, input, textarea, select, [role="button"], [role="link"]'
        );
        if (interactive) return;
        setIsManualOpen((prev) => !prev);
      }}
      onTouchStart={(e) => {
        if (disabled) return;
        const interactive = e.target.closest(
          'button, a, input, textarea, select, [role="button"], [role="link"]'
        );
        if (interactive) return;
        hadTouchStartRef.current = true;
        setTimeout(() => {
          hadTouchStartRef.current = false;
        }, 400);
        setIsManualOpen((prev) => !prev);
      }}
    >
      {triggerElement}
      {fixed ? (
        createPortal(
          <ReactTooltip
            id={tooltipIdRef.current}
            place={place}
            positionStrategy={fixed ? 'fixed' : 'absolute'}
            delayShow={isTabletOrMobile || isManualOpen ? 0 : timeout}
            delayHide={0}
            className={classNames(classes.reactTooltip, {
              [classes.fixed]: fixed,
            })}
            style={{
              width,
              whiteSpace: noWrap ? 'nowrap' : '',
              zIndex: fixed ? 100001 : undefined,
            }}
            wrapper="div"
            disable={disabled}
            clickable={isManualOpen}
            isOpen={isManualOpen ? isTooltipOpen : undefined}
            setIsOpen={isManualOpen ? setIsTooltipOpen : undefined}
          >
            {typeof text === 'string' ? text : <div>{text}</div>}
          </ReactTooltip>,
          document.body
        )
      ) : (
        <ReactTooltip
          id={tooltipIdRef.current}
          place={place}
          delayShow={isTabletOrMobile || isManualOpen ? 0 : timeout}
          delayHide={0}
          className={classNames(classes.reactTooltip, {
            [classes.fixed]: fixed,
          })}
          style={{
            width,
            whiteSpace: noWrap ? 'nowrap' : '',
            zIndex: fixed ? 100001 : undefined,
          }}
          wrapper="div"
          disable={disabled}
          clickable={isManualOpen}
          isOpen={isManualOpen ? isTooltipOpen : undefined}
          setIsOpen={isManualOpen ? setIsTooltipOpen : undefined}
        >
          {typeof text === 'string' ? text : <div>{text}</div>}
        </ReactTooltip>
      )}
    </div>
  );
}
